import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IProfile, IResponse, IRole, IUser } from '@interfaces/index';
import { AuthorizationService } from '@ui-components/services';
import { UserService } from '../../services/user.service';
import { Observable, switchMap } from 'rxjs';
import { IOrganization } from 'projects/ui-components/src/interfaces/IOrganization';
import { AppService } from '@ui-components/services';
import { RoleService } from '../../services/role.service';
import { OrganizationService } from '../../services/organization.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-user-modal',
  templateUrl: './user-modal.component.html',
  styleUrl: './user-modal.component.scss'
})
export class UserModalComponent {

  form: FormGroup
  loading = false;
  roles: IRole[] = [];
  organizations: IOrganization[] = [];
  profile: IProfile | null = null;
  hadExistingOllieLicense = false;
  removeOllieLicense = false;
  private initialOllieLicenseDate: string | null = null;

  get isSuperSuperAdmin(): boolean {
    return this._authorization_service.isSuperSuperAdmin(this.profile);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IUser,
    protected _app_service: AppService,
    protected _user_service: UserService,
    protected _role_service: RoleService,
    protected _organization_service: OrganizationService,
    protected _authorization_service: AuthorizationService,
    protected _fb: FormBuilder,
    protected _dialog_ref: MatDialogRef<UserModalComponent>
  ) {
    this.form = this._fb.group({
      first_name: [null],
      last_name: [null],
      email_address: [null, [Validators.email, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
      active: [true],
      org_id: [null],
      org_name: [null],
      role_id: [null],
      license_expiration_date: [null],
    })
  }

  ngOnInit() {
    this.loading = true;
    this.form.patchValue(this.data);
    this.initialOllieLicenseDate = this.data?.license_expiration_date ?? null;
    this.hadExistingOllieLicense = !!(this.data?.id && this.initialOllieLicenseDate);

    const licenseControl = this.form.get('license_expiration_date');
    licenseControl?.valueChanges.subscribe((value: string | null) => {
      if (!this.hadExistingOllieLicense) return;

      if (!this.removeOllieLicense && !value && this.initialOllieLicenseDate) {
        licenseControl.setValue(this.initialOllieLicenseDate, { emitEvent: false });
        return;
      }

      if (this.removeOllieLicense && !!value) {
        this.removeOllieLicense = false;
      }
    });

    this._app_service.getProfile().pipe(
      switchMap(
        (response: IResponse<IProfile>) => {
          this.profile = response.data;
          if (![1,2].includes(this.profile.role_id)) {
            this.form.patchValue({
              org_id: this.profile.org_id,
              role_id: 3
            })
          }
          return this._role_service.getRoles(this.profile.org_id);
        }
      ),
      switchMap(
        (response: IResponse<IRole[]>) => {
          this.roles = response.data;
          return this._organization_service.getOrgsById(this.profile?.org_id as number);
        }
      )
    ).subscribe(
      (response: IResponse<IOrganization[]>) => {
        this.organizations = response.data;
        this.form.get('org_name')?.setValue(this.organizations[0].name);
        this.loading = false;
      }
    )
  }

  saveUser() {
    const emailControl = this.form.get('email_address');
    if (emailControl?.invalid) {
      emailControl.markAsTouched();
      this._app_service.notify('Please enter a valid email before saving.', 'error');
      return;
    }

    let obs: Observable<IResponse<IUser>>;
    const userPayload = { ...this.form.value };

    if (this.isSuperSuperAdmin && !this.removeOllieLicense) {
      // Temporary business rule: always allocate Ollie product.
      userPayload['product_id'] = '4';
    } else {
      delete userPayload['license_expiration_date'];
    }

    if (this.data?.id) {
      if (this.removeOllieLicense) {
        userPayload['remove_ollie_license'] = true;
      }
      const user = { ...userPayload, user_id: this.data.id };
      obs = this._user_service.updateUser(user);
    } else {
      obs = this._user_service.createUser(userPayload);
    }

    obs.subscribe(
      () => {
        this._dialog_ref.close(true);
      },
      (response: HttpErrorResponse) => {
        this._app_service.notify(response.error.message as string, 'error');
      }
    );
  }

  clearOllieLicense(): void {
    if (!this.hadExistingOllieLicense) {
      return;
    }

    this.removeOllieLicense = true;
    this.form.patchValue({ license_expiration_date: null });
  }
}
